/**
 * @file JobsSpecificationCaching
 * @description
 *   Caches {@link CpmCJobsSpecificationApiModel} objects by job type so that
 *   repeated look‑ups avoid round‑trips to the server.
 *
 *   The cache is a simple plain‑object (`Record<string, …>`) whose keys are
 *   the `job_type` strings returned by the API.  All cache operations are
 *   performed via static members, which means the cache is shared across
 *   the entire application without the need for an Angular service instance.
 *
 *   The main entry point is {@link JobsSpecificationCaching.getExecutorActiveSpecification}.
 *   It returns an {@link Observable} that emits **exactly one** `CpmCJobsSpecificationApiModel`
 *   for the requested job type.  If the specification is already cached it is
 *   served immediately; otherwise the API is called, the result is cached,
 *   and the observable resolves with the newly fetched value.
 *
 *   The implementation intentionally uses RxJS operators only where
 *   necessary – the observable chain is short and side‑effect free.
 *
 * @example
 * ```ts
 * // Request the active specification for the 'transcode' job type.
 * JobsSpecificationCaching.getExecutorActiveSpecification('transcode')
 *   .subscribe(spec => console.log('Active spec:', spec));
 *
 * // Subsequent calls for the same job type hit the cache instantly.
 * JobsSpecificationCaching.getExecutorActiveSpecification('transcode')
 *   .subscribe(spec => console.log('Cached spec:', spec));
 * ```
 */
import {map, Observable, of, tap} from 'rxjs';
import {CpmCJobsSpecificationApiModel} from '../../../gen/cpm-c-api-ts/src/models/c-global/cpmc-jobs-specification.api-model';
import {CpmCJobsSpecificationApiService} from '../../../gen/cpm-c-api-ts/src/services/c-global/cpmc-jobs-specification.api-service';
import {JobsUtils} from '../../../sdk/src/utils/jobs.utils';

/**
 * Simple in‑memory cache for `CpmCJobsSpecificationApiModel` objects keyed by job type.
 */
export abstract class JobsSpecificationCaching {
    private static activeSpecifications: Record<string, CpmCJobsSpecificationApiModel> = {};
    private static activeSpecificationsAppDetails: Record<string, Record<string, unknown>> = {};

    /**
     * Return the active specification for the given job type.
     *
     * The method works in two phases:
     *
     * 1. **Cache lookup** – If the requested `jobType` is already present
     *    in the static `activeSpecifications` cache, the method immediately
     *    returns an `Observable` that emits the cached instance (`of(cached)`).
     *
     * 2. **Server fetch** – If the specification is missing from the cache,
     *    `CpmCJobsSpecificationApiService.getExecutorActiveSpecification`
     *    is called.  The resulting observable is piped through a
     *    `tap` side‑effect that stores the fetched specification in the
     *    cache under its `job_type`.  The same observable is then returned
     *    to the caller so that the subscription receives the freshly
     *    retrieved value.
     *
     * @param jobType The string identifier of the job type whose specification is desired.
     * @returns `Observable<CpmCJobsSpecificationApiModel>` that emits the active specification.
     *
     * @example
     * ```ts
     * // Fetch and cache the spec for 'thumbnail'
     * JobsSpecificationCaching.getExecutorActiveSpecification('thumbnail')
     *   .subscribe(spec => console.log('Spec:', spec));
     * ```
     */
    public static getExecutorActiveSpecification(jobType: string): Observable<CpmCJobsSpecificationApiModel> {
        // If nothing is missing, serve from cache immediately.
        if (jobType in this.activeSpecifications) {
            return of(this.activeSpecifications[jobType]);
        }
        // Build the API request for the missing jobType.
        const observable = CpmCJobsSpecificationApiService.getExecutorActiveSpecification(jobType);
        // Cache the response and return a merged result.
        return observable.pipe(
            // Side‑effect: store every fetched specification in the static cache.
            tap((fetchedSpec: CpmCJobsSpecificationApiModel) => {
                this.activeSpecifications[fetchedSpec.job_type] = fetchedSpec;
            })
        );
    }

    /**
     * Return the deserialized app details from the active specification for the given job type.
     *
     * This method builds upon {@link getExecutorActiveSpecification} to extract and cache
     * the `app_details` portion of a job specification. It works in two phases:
     *
     * 1. **Cache lookup** – If the app details for the requested `jobType` are already
     *    present in the static `activeSpecificationsAppDetails` cache, the method
     *    immediately returns an `Observable` that emits the cached object (`of(cached)`).
     *
     * 2. **Server fetch and deserialize** – If the app details are missing from the cache,
     *    the method calls {@link getExecutorActiveSpecification} to retrieve the full
     *    specification. The resulting observable is piped through a `map` operator that:
     *    - Extracts the `app_details` from the specification's `content` field
     *    - Deserializes it using `JobsUtils.deserializeSpecificationAppDetails`
     *    - Stores the result in the `activeSpecificationsAppDetails` cache
     *    - Returns the deserialized app details object
     *
     * This approach ensures that app details are only deserialized once per job type,
     * improving performance for repeated access to the same specification's app details.
     *
     * @param jobType The string identifier of the job type whose app details are desired.
     * @returns `Observable<Record<string, unknown>>` that emits the deserialized app details.
     *
     * @example
     * ```ts
     * // Fetch, deserialize, and cache the app details for 'transcode'
     * JobsSpecificationCaching.getExecutorActiveSpecificationAppDetails('transcode')
     *   .subscribe(appDetails => console.log('App details:', appDetails));
     *
     * // Subsequent calls hit the cache instantly without re-deserializing
     * JobsSpecificationCaching.getExecutorActiveSpecificationAppDetails('transcode')
     *   .subscribe(appDetails => console.log('Cached app details:', appDetails));
     * ```
     */
    public static getExecutorActiveSpecificationAppDetails(jobType: string): Observable<Record<string, unknown>> {
        // If nothing is missing, serve from cache immediately.
        if (jobType in this.activeSpecificationsAppDetails) {
            return of(this.activeSpecificationsAppDetails[jobType]);
        }

        // Build the API request for the missing jobType.
        const observable = JobsSpecificationCaching.getExecutorActiveSpecification(jobType);
        // Cache the response and return a merged result.
        return observable.pipe(
            // Side‑effect: store every fetched specification in the static cache.
            map((fetchedSpec: CpmCJobsSpecificationApiModel) => {
                this.activeSpecificationsAppDetails[fetchedSpec.job_type] =
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    JobsUtils.deserializeSpecificationAppDetails((fetchedSpec.content as any)['app_details']);

                return this.activeSpecificationsAppDetails[fetchedSpec.job_type];
            })
        );
    }
}
